import { Icon } from '@/components/Icon'
import type { Fleet, NodeState, RunNode, Rollout, TaskInfo } from '@/lib/runs'

/** A node stages the program image once, before its agent exists; each job then
 * stages its own task assets and guest. Two jobs on one node differ here. */
export const PREPARING_JOB = 'Fetching task assets, restoring the guest and running the task’s setup hooks.'

/** One vocabulary for a node's state, shared with the node panel. */
export const NODE_STATES: Record<NodeState, { label: string; tone: 'neutral' | 'ok' | 'warn' | 'err' }> = {
  pending: { label: 'Requested', tone: 'neutral' },
  booting: { label: 'Booting', tone: 'warn' },
  staging: { label: 'Staging image', tone: 'warn' },
  starting: { label: 'Starting worker', tone: 'warn' },
  running: { label: 'Running jobs', tone: 'ok' },
  idle: { label: 'Idle', tone: 'ok' },
  draining: { label: 'Draining', tone: 'neutral' },
  unreachable: { label: 'Unreachable', tone: 'err' },
  stopped: { label: 'Stopped', tone: 'neutral' },
}

/** The agent's step against the task's own step budget. */
export function StepMeter({ step, maxSteps, className = '' }: { step?: number; maxSteps?: number | null; className?: string }) {
  if (!maxSteps) return step ? <span className={`step-meter ${className}`}>Step {step}</span> : null
  const done = Math.min(step ?? 0, maxSteps)
  return <span className={`step-meter ${className}`} title={`Step ${done} of the task's ${maxSteps}-step budget`}>
    <span className="step-meter-count">Step {done}/{maxSteps}</span>
    <span className="step-meter-bar" aria-hidden="true"><i style={{ width: `${(done / maxSteps) * 100}%` }} /></span>
  </span>
}

function seconds(value?: number | null) {
  if (value == null) return '—'
  if (value < 90) return `${Math.round(value)}s`
  const minutes = Math.floor(value / 60)
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

function lead(fleet: Fleet): string {
  if (fleet.launch_state === null && !fleet.launched) return 'Preparing the experiment: pinning tasks, staging code, and building the job manifest.'
  if (fleet.launch_state === 'prepared') return 'The job manifest is written; the jobs are being put on the queue.'
  if (fleet.launch_state === 'jobs_enqueued') return 'The jobs are on the queue. The worker nodes have not been launched yet.'
  const parts: string[] = []
  if (fleet.polling) parts.push(`${fleet.polling} of ${fleet.requested} polling the queue`)
  if (fleet.preparing) parts.push(`${fleet.preparing} still booting or staging the program image`)
  if (fleet.unreachable) parts.push(`${fleet.unreachable} unreachable`)
  if (fleet.stopped) parts.push(`${fleet.stopped} stopped`)
  if (!parts.length) return `${fleet.requested} node(s) requested; none has reported yet.`
  return `${parts.join(' · ')} · ${fleet.busy_slots}/${fleet.slots} worker slots busy`
}

function NodeCard({ node, jobs, tasks, selected, onSelect, onSelectNode }: {
  node: RunNode; jobs: Map<string, Rollout>; tasks: Map<string, TaskInfo>; selected: boolean
  onSelect: (jobId: string) => void; onSelectNode: (instanceId: string) => void
}) {
  const state = NODE_STATES[node.state] ?? NODE_STATES.pending
  return <article className={`node-card node-card-${node.state}${selected ? ' selected' : ''}`}>
    <button className="node-open" type="button" aria-pressed={selected}
      aria-label={`Node ${node.instance_id}, ${state.label}`} onClick={() => onSelectNode(node.instance_id)}>
      <span><Icon name="server" size={16} /><strong>{node.instance_id}</strong></span>
      <span className={`node-status ${state.tone}`}>{state.label}<Icon name="arrow" size={15} /></span>
    </button>
    <p className="node-activity">{node.activity}</p>
    {node.slots > 0 && <div className="node-slots" title={`${node.busy} of ${node.slots} concurrent job slots in use`}>
      <span className="muted small">Slots</span>
      <span className="node-capacity" role="img" aria-label={`${node.busy} of ${node.slots} slots busy`}><i style={{ width: `${node.busy / node.slots * 100}%` }} /></span>
      <span className="small">{node.busy}/{node.slots}</span>
    </div>}
    {node.running.length > 0 && <ul className="node-jobs">{node.running.map(job => {
      const row = job.job_id ? jobs.get(job.job_id) : undefined
      return <li key={job.attempt_id ?? `${job.task}-${job.seed}`}>
        <button type="button" className="node-job" disabled={!job.job_id}
                onClick={event => { event.stopPropagation(); if (job.job_id) onSelect(job.job_id) }}>
          <span className="node-job-task" title={job.task}>{tasks.get(job.task)?.title ?? job.task}</span>
          {row?.phase === 'preparing'
            ? <span className="job-phase" title={PREPARING_JOB}>preparing</span>
            : <StepMeter step={job.latest_sequence} maxSteps={row?.max_steps} />}
          <span className="muted small">{seconds(job.elapsed_s)}</span>
        </button>
      </li>
    })}</ul>}
    <footer className="node-counters">
      <span><strong>{node.completed}</strong> done</span>
      <span className={node.infra_total ? 'err-text' : ''}><strong>{node.infra_total}</strong> infra</span>
      {node.rate_limited > 0 && <span><strong>{node.rate_limited}</strong> rate-limited</span>}
      {node.infra_streak > 0 && <span className="err-text" title="Ten consecutive node-attributable failures quarantine the node">streak {node.infra_streak}/10</span>}
      <span className="muted">{node.age_seconds == null ? 'no heartbeat' : `${seconds(node.age_seconds)} ago`}</span>
    </footer>
  </article>
}

/** What the fleet is doing right now: which nodes exist, how far each got
 * through its boot, and which jobs each of them is running. */
export function RunFleet({ fleet, nodes, jobs, tasks, selectedNodeId, onSelectJob, onSelectNode }: {
  fleet: Fleet; nodes: RunNode[]; jobs: Rollout[]; tasks: Map<string, TaskInfo>
  selectedNodeId?: string
  onSelectJob: (jobId: string) => void; onSelectNode: (instanceId: string) => void
}) {
  const byJobId = new Map(jobs.map(job => [job.job_id, job]))
  return <section className="run-fleet" aria-labelledby="run-fleet-title">
    <div className="section-head">
      <div><h2 id="run-fleet-title">Workers</h2><p className="muted small">{lead(fleet)}</p></div>
      <span className="muted small">{fleet.reporting}/{fleet.requested} reporting</span>
    </div>
    {nodes.length ? <div className="node-grid">{nodes.map(node => <NodeCard key={node.instance_id} node={node} jobs={byJobId} tasks={tasks}
                                                                 selected={node.instance_id === selectedNodeId}
                                                                 onSelect={onSelectJob} onSelectNode={onSelectNode} />)}</div>
      : <p className="empty">Waiting for the first worker heartbeat. Provisioning can take a few minutes.</p>}
  </section>
}
